import { useState, useEffect } from 'react';
import { useTranslation } from '@parsina/framework/clinical/src/i18n/index';
import { C, soft } from '@parsina/framework/tokens';
import { MoneyInput } from '@parsina/framework';
import { fmtCoins, formatCreditMoney, isTomanCreditPrice } from '@parsina/framework/clinical/src/constants/creditPricing';
import { useCreditPrices } from '@parsina/framework/clinical/src/lib/useCreditPrices';
import { type } from '@parsina/framework/clinical/src/constants/typography';
import { gatewayLabel } from '../../constants/paymentGateway';
import { CoinGlyph } from '@parsina/framework/clinical/src/components/common/CoinChip';

// Inner content for the «خرید پارسه» flow — rendered inside a Dialog on desktop and
// a BottomSheet on mobile. Coins are the product; money is shown only as the
// total to pay. Packages are explained in work done ("≈ N visits"), per CORE.md.
//
// The packages and the price of one credit are the deployment's when the BFF
// names them (`creditPrices.packages` / `.creditPrice`, PM-0211), else the list
// below and the toman price, as always. The middle package is the recommended
// one and the one picked on opening (500 of 200/500/1,000). A price in another
// currency is printed in it (`formatCreditMoney`), never in toman, and has no
// custom amount: the field below is a toman field.

const PACKAGES = [200, 500, 1000];
// The floor is ONE coin (see `minToman` below). It exists to stop trivially
// small gateway transactions, not to force a package: a doctor who wants a
// single coin's worth should be able to buy it, and every Iranian gateway
// settles amounts this size happily.

// Fallback platform assumption (~45 coins/visit) when the doctor has no history.
const FALLBACK_COST_PER_VISIT = 45;

function fmtTomanPlain(toman, formatNumber) {
  return formatNumber(Number(toman || 0));
}

/**
 * @param avgPerVisit personal coins/visit (null → platform fallback)
 * @param onPay(coins, toman) → begins payment; parent handles redirect/errors
 */
export default function TopUpSheet({ avgPerVisit, balance, onPay, busy = false, error = null }) {
  const { t, formatNumber, locale } = useTranslation();
  // What one پارسه costs: in Toman the active policy's figure, else the constant;
  // in another currency the wallet's, in its minor units.
  const { coinPriceToman: COIN_PRICE_TOMAN, creditPrice, packages: deploymentPackages } = useCreditPrices();
  const inToman = isTomanCreditPrice(creditPrice);
  const unitPrice = inToman ? COIN_PRICE_TOMAN : creditPrice.amountMinor;
  const packages = deploymentPackages?.length ? deploymentPackages : PACKAGES;
  const recommended = packages[Math.floor(packages.length / 2)];
  const money = (minor) => formatCreditMoney(minor, creditPrice.currency, { formatNumber, locale });
  const minToman = COIN_PRICE_TOMAN;
  const [selected, setSelected] = useState(recommended);   // coins, or 'custom'
  const [customToman, setCustomToman] = useState('');
  // The deployment's packages can land after the sheet opened: a pick that is
  // no longer offered moves to the recommended one.
  useEffect(() => {
    if (selected !== 'custom' && !packages.includes(selected)) setSelected(recommended);
  }, [packages, recommended, selected]);

  const perVisit = avgPerVisit && avgPerVisit > 0 ? avgPerVisit : FALLBACK_COST_PER_VISIT;
  const customCoins = Math.floor((Number(customToman) || 0) / COIN_PRICE_TOMAN);
  const isCustom = inToman && selected === 'custom';
  const coins = isCustom ? customCoins : selected;
  // Minor units of the credit's currency — toman for Parsina, as the name says.
  const toman = coins * unitPrice;
  const belowMin = inToman ? toman < minToman : coins <= 0;
  const visits = Math.round(coins / perVisit);

  const rowStyle = (active) => ({
    display: 'flex', alignItems: 'center', gap: 10, width: '100%',
    border: `1.5px solid ${active ? C.teal : C.border}`,
    background: active ? soft(C.teal, 0.08) : C.card,
    borderRadius: 12, padding: '11px 14px', cursor: 'pointer', textAlign: 'start',
    transition: 'border-color 0.15s, background 0.15s',
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {packages.map(pkg => {
        const active = !isCustom && selected === pkg;
        return (
          <button key={pkg} onClick={() => setSelected(pkg)} style={rowStyle(active)} data-testid={`topup-pkg-${pkg}`}>
            <span style={{ color: C.teal, display: 'flex' }}><CoinGlyph size={16} /></span>
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ ...type.amount, color: C.textPrimary }}>{fmtCoins(pkg, formatNumber)} {t('common.units.coin')}</span>
                {pkg === recommended && (
                  <span style={{ ...type.micro, fontWeight: 800, color: C.green, background: C.greenLight, borderRadius: 999, padding: '1px 8px' }}>{t('credits.recommended')}</span>
                )}
              </div>
              <div style={{ ...type.caption, color: C.textMuted, marginTop: 2 }}>
                ≈ {formatNumber(Math.round(pkg / perVisit))} {t('credits.visitsWithAi')}
              </div>
            </div>
            <span style={{ ...type.label, fontWeight: 700, color: C.textSecondary, whiteSpace: 'nowrap' }}>{inToman ? <>{fmtTomanPlain(pkg * COIN_PRICE_TOMAN, formatNumber)} {t('common.units.tomanShort')}</> : money(pkg * unitPrice)}</span>
          </button>
        );
      })}

      {/* Custom amount — a toman field, so only for a toman price. */}
      {inToman && (
      <button onClick={() => setSelected('custom')} style={rowStyle(isCustom)} data-testid="topup-custom">
        <span style={{ color: isCustom ? C.teal : C.textMuted, display: 'flex' }}><CoinGlyph size={16} /></span>
        <div style={{ flex: 1 }}>
          <div style={{ ...type.body, color: C.textPrimary }}>{t('credits.customAmount')}</div>
          {isCustom && coins > 0 && (
            <div style={{ ...type.caption, color: C.textMuted, marginTop: 2 }}>= {fmtCoins(coins, formatNumber)} {t('common.units.coin')}</div>
          )}
        </div>
        {isCustom && (
          // The «تومان» chip is the field's own now, so the separate span that
          // used to sit beside it is gone; the row still constrains the width,
          // which the sheet's layout depends on.
          <div onClick={e => e.stopPropagation()}
            style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
            <MoneyInput
              value={customToman}
              onChange={setCustomToman}
              autoFocus
              aria-label={t('credits.customAmountToman')}
              unitLabel={t('common.units.toman')}
              inputStyle={{
                ...type.body, width: 148, boxSizing: 'border-box',
                padding: '7px 10px', paddingInlineStart: 46, textAlign: 'end',
                border: `1px solid ${C.border}`, borderRadius: 8,
                background: C.card, color: C.textPrimary, outline: 'none',
                fontFamily: 'inherit',
              }}
            />
          </div>
        )}
      </button>
      )}

      {error && (
        <div style={{ ...type.label, color: C.red, background: C.redLight, borderRadius: 8, padding: '8px 12px' }}>
          {error}
        </div>
      )}

      <button
        onClick={() => !belowMin && !busy && onPay(coins, toman)}
        disabled={belowMin || busy}
        data-testid="topup-pay"
        style={{
          ...type.amount,
          marginTop: 4, width: '100%', boxSizing: 'border-box',
          height: 46, borderRadius: 12, border: 'none',
          background: belowMin || busy ? soft(C.navy, 0.4) : C.navy, color: '#fff',
          cursor: belowMin || busy ? 'not-allowed' : 'pointer',
        }}
      >
        {busy
          ? t('credits.redirecting')
          : toman > 0
            // «پرداخت N تومان — درگاه» names toman and an Iranian bank; another
            // currency's button names the amount alone.
            ? (inToman ? t('credits.payVia', { amount: fmtTomanPlain(toman, formatNumber), gateway: gatewayLabel(t) }) : `${t('credits.buyCoins')} · ${money(toman)}`)
            : t('validation.amount.required')}
      </button>
      <div style={{ ...type.micro, fontWeight: 600, color: C.textMuted, textAlign: 'center' }}>
        {belowMin && inToman
          ? t('credits.minPurchase', { amount: fmtTomanPlain(minToman, formatNumber) })
          : t('credits.redirectNote')}
      </div>
    </div>
  );
}
